import { WorkerPool } from './WorkerPool';
import * as msgpack from '@msgpack/msgpack';

export interface CalcPayload {
    itemUuid: string;
    method: string;
    reductions?: string[];
    workflows?: any;
    required?: string[];
    assetUuids?: string[];
    insurerIds: string[];
}

export class EngineService {
    private pool: WorkerPool | null = null;
    private itemsBuffer: ArrayBuffer | null = null;
    private staticBuffer: ArrayBuffer | null = null;
    private engineSource: string | null = null;
    private insurersRef: any[] = [];

    constructor() {
    }

    get isEngineLoaded(): boolean {
        return this.pool !== null && this.engineSource !== null;
    }

    async fetchEngineSource(remoteUrl: string): Promise<string> {
        const response = await fetch(remoteUrl);
        const text = await response.text();
        const serverHash = response.headers.get('X-Engine-Hash');
        if (serverHash) {
            try {
                const encoder = new TextEncoder();
                const data = encoder.encode(text);
                const hashBuffer = await crypto.subtle.digest('SHA-256', data);
                const localHash = Array.from(new Uint8Array(hashBuffer))
                    .map(b => b.toString(16).padStart(2, '0'))
                    .join('');
                if (localHash !== serverHash) {
                    throw new Error('Engine integrity check failed');
                }
            } catch (e: any) {
                if (e.message === 'Engine integrity check failed') throw e;
                console.warn('Hash verification unavailable', e);
            }
        }
        return text;
    }

    async loadEngine(remoteUrl: string): Promise<string> {
        if (this.pool) {
            console.warn('[EngineService] Engine déjà chargé, skip');
            return this.engineSource!;
        }
        this.engineSource = await this.fetchEngineSource(remoteUrl);
        this.pool = new WorkerPool('/workers/calculator.worker.js', remoteUrl, undefined, 0.5);
        await this.pool.booted();
        await this.pool.injectSource(this.engineSource);
        return this.engineSource;
    }

    async setStaticData(itemTypes: any, insurers: any[]): Promise<void> {
        if (!this.pool || !this.engineSource) return;
        this.insurersRef = insurers;
        const packed = msgpack.encode({ itemTypes, insurers });
        this.staticBuffer = packed.buffer.slice(packed.byteOffset, packed.byteOffset + packed.byteLength);
        this.pool.broadcastStaticBuffer(this.staticBuffer);
        this.pool.setInsurers(insurers);
    }

    async setItems(items: any): Promise<void> {
        if (!this.pool || !this.engineSource) return;
        const packed = msgpack.encode({ items });
        this.itemsBuffer = packed.buffer.slice(packed.byteOffset, packed.byteOffset + packed.byteLength);
        this.pool.broadcastBuffer(this.itemsBuffer);
    }

    calculate(
        ids: string | string[],
        payload: any,
        onCoverage?: (cov: any) => void
    ): Promise<Record<string, any>> {
        if (!this.pool || !this.itemsBuffer) throw new Error('Engine or data not ready');
        const list = typeof ids === 'string' ? [ids] : ids;
        return this.pool.exec(
            { method: payload.method, payload: { ...payload, insurerIds: list } },
            onCoverage || (() => {})
        );
    }

    calculatePerInsurer(
        ids: string[],
        payload: any,
        onResult?: (result: any) => void
    ): Promise<void> {
        if (!this.pool || !this.itemsBuffer) throw new Error('Engine or data not ready');
        return this.pool.execPerInsurer(
            {method: payload.method, payload: {...payload, insurerIds: ids}},
            onResult || (() => {})
        );
    }

    shutdown(): void {
        if (this.pool) {
            this.pool.terminate();
            this.pool = null;
        }

        // Libération explicite des buffers
        if (this.itemsBuffer) {
            this.itemsBuffer = null;
        }
        if (this.staticBuffer) {
            this.staticBuffer = null;
        }

        this.engineSource = null;
        this.insurersRef = [];

        // Force garbage collection si disponible
        if (typeof window !== 'undefined' && (window as any).gc) {
            try {
                (window as any).gc();
            } catch (e) {
                console.warn('[EngineService] GC failed:', e);
            }
        }
    }
}

export const engineService = new EngineService();